import { useMutation } from '@tanstack/react-query';
import { Col, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { PlantApi } from 'apis/PlantApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { FormFilter, SelectForm } from 'components';
import RangePickerForm from 'components/RangePicker/RangePickerForm';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import { IFilterTeamSaleCb } from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';
import { useEffect, useMemo, useState } from 'react';

const spanCol = 8;

export interface FilterTeamRevenueProps {
  defaultFilter?: any;
  onFilter?: (values: any) => void;
  onCancel?: () => void;
}

const FilterTeamRevenue = ({
  defaultFilter,
  onCancel,
  onFilter,
}: FilterTeamRevenueProps) => {
  const { user } = useUser();
  const [form] = useForm();
  const [plants, setPlants] = useState<DefaultOptionType[]>([]);
  const [teamSaleData, setTeamSaleData] =
    useState<ILazySelectData>(initLazySelectData);

  const teamOptions = useMemo(
    () =>
      ((user as any)?.teamSales ?? []).map((item: any) => ({
        label: item.subId + ' - ' + item.name,
        value: item.subId,
      })) ?? [],
    [user],
  );

  const { mutate: getAllPlant, isPending: getAllPlantLoading } = useMutation({
    mutationFn: () => PlantApi.getAllPlantComboBox(),
    onSuccess: (data: any) => {
      const items = data.data?.getPlant?.items || [];

      if (!items?.length) return setPlants([]);

      setPlants(
        items.map((item: any) => ({
          label: item.id,
          value: item.id,
        })),
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { isPending: getTeamSaleLoading, mutate: getTeamSales } = useMutation({
    mutationFn: (params: IFilterTeamSaleCb) =>
      SaleTeamApi.getAllTeamSaleCbSubSort(params),
    onSuccess: (res) => {
      if (res?.data?.getTeamSales) {
        const items = res.data?.getTeamSales?.items ?? [],
          totalItems = res.data?.getTeamSales?.pageInfo?.totalItems ?? 0;

        setTeamSaleData({
          options: items.map((item: any) => ({
            label: item.subId + ' - ' + item.name,
            value: `${item.id}.${item.subId}.${item.plantId}`,
          })),
          totalItems: totalItems,
        });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleTeamSaleScrollChange = (params: ILazySelectParams) => {
    const plant = form.getFieldValue('plantId');
    const teamSaleParams: IFilterTeamSaleCb = {
      ...params,
      plantId: plant || defaultFilter.plantId,
    };

    getTeamSales(teamSaleParams);
  };

  const handleChangePlant = (value: any) => {
    form.setFieldsValue({
      teamSalesId: undefined,
      routeId: undefined,
      plantId: value,
    });

    handleTeamSaleScrollChange({
      pageSize: 20,
      value: '',
    });
  };

  const handleFinish = (values: any) => {
    onFilter?.(values);
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel?.();
  };

  useEffect(() => {
    getAllPlant();
  }, [getAllPlant]);

  return (
    <Content isOpen>
      <FormFilter
        okTitle="Xem báo cáo"
        form={form}
        onFinish={handleFinish}
        onCancel={handleCancel}
      >
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item
              label="Chi nhánh"
              name="plantId"
              rules={[{ required: true, message: 'Vui lòng chọn chi nhánh' }]}
            >
              <SelectForm
                loading={getAllPlantLoading}
                options={plants}
                placeholder="Chọn chi nhánh"
                disabled={user?.plantCode !== '5000'}
                onChange={handleChangePlant}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item
              label="Đội bán hàng"
              name="teamSalesId"
              initialValue={defaultFilter?.teamSalesSubId}
            >
              {user?.isSale ? (
                <SelectForm
                  disabled
                  placeholder="Chọn đội"
                  options={teamOptions}
                />
              ) : (
                <LazySelectForm
                  customeSize={20}
                  data={teamSaleData}
                  placeholder="Chọn đội"
                  loading={getTeamSaleLoading}
                  onSelectScrollChange={handleTeamSaleScrollChange}
                />
              )}
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item
              label="Từ ngày - đến ngày"
              name="dateRange"
              rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
            >
              <RangePickerForm
                format={{
                  format: 'DD/MM/YYYY',
                }}
                placeholder={['Từ ngày', 'Đến ngày']}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
};

export default FilterTeamRevenue;
